import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGraduationCap, faHome, faSignOutAlt, faSignInAlt, faUserPlus, 
  faMoon, faSun, faChevronDown, faBook, faUser, faCog, faCreditCard, faChevronUp, faDollarSign,
  faArrowRight, faSearch, faLayerGroup, faCertificate, faStar, faRocket, faQuestionCircle, faBars, faTimes, faTicketAlt, faCrown, faRobot
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import BrandLogo from './BrandLogo';
import api from '../config/api';
import { getExamUrl } from '../utils/slugify';

interface Subcategory {
  subcategory: string | null;
  exams: Array<{
    id: number;
    name: string;
    badge_image?: string;
  }>;
}

interface Category {
  category: string;
  subcategories: Subcategory[];
}

interface Exam {
  id: number;
  name: string;
  badge_image?: string;
  subcategory?: string | null;
}

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [allExams, setAllExams] = useState<Exam[]>([]);
  const [examsBySubcategory, setExamsBySubcategory] = useState<Map<string, Exam[]>>(new Map());
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [freeModeEnabled, setFreeModeEnabled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchCategories();
    fetchAllExams();
    fetchFreeModeStatus();
  }, []);

  const fetchFreeModeStatus = async () => {
    try {
      const response = await api.get('/settings/free-mode/status');
      setFreeModeEnabled(response.data.freeModeEnabled || false);
    } catch (error) {
      console.error('Failed to fetch free mode status:', error);
      // Default to false if check fails
      setFreeModeEnabled(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setShowMobileMenu(false);
      }
    };

    if (showDropdown || showUserMenu || showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, showUserMenu, showMobileMenu]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/exams/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchAllExams = async () => {
    try {
      // Use the categories endpoint which we know works
      const response = await api.get('/exams/categories');
      const exams: Exam[] = [];
      const subcategoryMap = new Map<string, Exam[]>();
      
      if (response.data.categories) {
        response.data.categories.forEach((category: Category) => {
          category.subcategories.forEach((subcategory: Subcategory) => {
            const subcategoryKey = subcategory.subcategory || 'Other';
            if (!subcategoryMap.has(subcategoryKey)) {
              subcategoryMap.set(subcategoryKey, []);
            }
            
            subcategory.exams.forEach((exam: any) => {
              const examData: Exam = {
                id: exam.id,
                name: exam.name,
                badge_image: exam.badge_image,
                subcategory: subcategory.subcategory
              };
              exams.push(examData);
              subcategoryMap.get(subcategoryKey)!.push(examData);
            });
          });
        });
      }
      
      setAllExams(exams);
      setExamsBySubcategory(subcategoryMap);
    } catch (error) {
      console.error('Failed to fetch all exams:', error);
      setAllExams([]);
      setExamsBySubcategory(new Map());
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get user initials from fullName or email
  const getUserInitials = (): string => {
    if (user?.fullName) {
      const names = user.fullName.trim().split(/\s+/);
      if (names.length >= 2) {
        // First letter of first name + first letter of last name
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      } else if (names.length === 1) {
        // Single name: use first two letters
        return names[0].substring(0, 2).toUpperCase();
      }
    }
    // Fallback to email initials
    if (user?.email) {
      const emailParts = user.email.split('@')[0];
      if (emailParts.length >= 2) {
        return emailParts.substring(0, 2).toUpperCase();
      }
      return emailParts[0].toUpperCase();
    }
    return 'U';
  };

  // Generate a color based on user's name/email for consistent avatar color
  const getAvatarColor = (): string => {
    const str = user?.fullName || user?.email || 'user';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Brand-adjacent palette — no blue/indigo/purple
    const colors = [
      'bg-teal-600', 'bg-emerald-600', 'bg-cyan-700', 'bg-amber-600',
      'bg-orange-600', 'bg-rose-600', 'bg-green-600', 'bg-lime-700',
      'bg-stone-600', 'bg-red-600', 'bg-yellow-600', 'bg-teal-700'
    ];
    return colors[Math.abs(hash) % colors.length];
  };

  const examPlaceholderStyle = {
    background: 'linear-gradient(135deg, var(--gp-brand), #17838d)',
  };

  return (
    <nav className="gp-nav sticky top-0 z-50">
      <div className="gp-container">
        <div className="flex justify-between items-center h-12">
          <BrandLogo to={user ? '/dashboard' : '/'} size="sm" />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {user ? (
              <>
                <Link to="/dashboard" className="gp-nav-link whitespace-nowrap">
                  <FontAwesomeIcon icon={faHome} />
                  <span>{t("dashboard.title")}</span>
                </Link>
                <Link to="/learn" className="gp-nav-link whitespace-nowrap">
                  <FontAwesomeIcon icon={faRobot} />
                  <span>{t('nav.learn')}</span>
                </Link>
                
                {/* Certifications Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="gp-nav-link group"
                  >
                    <FontAwesomeIcon icon={faGraduationCap} className="group-hover:scale-110 transition-transform" />
                    <span>{t('nav.certifications')}</span>
                    <FontAwesomeIcon 
                      icon={faChevronDown} 
                      className={`text-xs transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
                    />
                  </button>
                  
                  {showDropdown && (
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 w-screen max-w-3xl z-50 overflow-hidden hidden md:block"
                      style={{
                        background: 'var(--gp-surface)',
                        border: '1px solid var(--gp-border)',
                        borderRadius: 'var(--gp-radius)',
                        boxShadow: 'var(--gp-shadow-lg)',
                      }}
                    >
                      {/* Compact Header */}
                      <div
                        className="px-3 py-2.5 border-b"
                        style={{
                          background: 'var(--gp-brand-soft)',
                          borderColor: 'var(--gp-border)',
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faCertificate} className="text-sm" style={{ color: 'var(--gp-brand)' }} />
                            <h3 className="text-sm font-bold" style={{ color: 'var(--gp-ink)' }}>All Certifications</h3>
                            <span className="text-xs" style={{ color: 'var(--gp-ink-subtle)' }}>({allExams.length})</span>
                          </div>
                          <Link
                            to="/certifications"
                            onClick={() => setShowDropdown(false)}
                            className="text-xs font-semibold flex items-center gap-1 hover:underline"
                            style={{ color: 'var(--gp-brand)' }}
                          >
                            <span>View All</span>
                            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                          </Link>
                        </div>
                      </div>

                      {/* All Certifications Grid with Logos - Organized by Subcategory */}
                      <div className="p-2 max-h-[600px] overflow-y-auto">
                        {Array.from(examsBySubcategory.entries()).map(([subcategory, exams]) => (
                          <div key={subcategory} className="mb-2.5 last:mb-0">
                            {/* Subcategory Header */}
                            {subcategory !== 'Other' && (
                              <div className="mb-1 px-1">
                                <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--gp-ink-subtle)' }}>
                                  {subcategory}
                                </h4>
                              </div>
                            )}
                            {/* Exams Grid */}
                            <div className="grid grid-cols-2 gap-1">
                              {exams.map((exam) => (
                                <Link
                                  key={exam.id}
                                  to={getExamUrl(exam.id, exam.name)}
                                  onClick={() => setShowDropdown(false)}
                                  className="flex items-center gap-2 p-1.5 rounded-md transition-colors group hover:bg-[var(--gp-brand-soft)]"
                                >
                                  {/* Logo */}
                                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                                    {exam.badge_image ? (
                                      <img
                                        src={`/${exam.badge_image}`}
                                        alt={exam.name}
                                        className="w-8 h-8 object-contain"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                          const fallback = target.nextElementSibling as HTMLElement;
                                          if (fallback) fallback.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div
                                      className={`w-8 h-8 rounded-md flex items-center justify-center ${exam.badge_image ? 'hidden' : ''}`}
                                      style={examPlaceholderStyle}
                                    >
                                      <FontAwesomeIcon icon={faGraduationCap} className="text-white text-xs" />
                                    </div>
                                  </div>
                                  {/* Name */}
                                  <div className="flex-1 min-w-0">
                                    <p
                                      className="text-xs font-medium leading-tight transition-colors group-hover:text-[var(--gp-brand)]"
                                      style={{ color: 'var(--gp-ink)' }}
                                    >
                                      {exam.name}
                                    </p>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* User Settings Dropdown */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="gp-nav-link gap-2"
                  >
                    <div className={`w-7 h-7 rounded-full ${getAvatarColor()} flex items-center justify-center text-white font-semibold text-xs ring-2 ring-[var(--gp-surface)]`}>
                      {getUserInitials()}
                    </div>
                    <FontAwesomeIcon
                      icon={showUserMenu ? faChevronUp : faChevronDown}
                      className="text-xs"
                    />
                  </button>

                  {showUserMenu && (
                    <div className="gp-dropdown w-56">
                      <div className="px-3 py-2.5 border-b" style={{ borderColor: 'var(--gp-border)' }}>
                        <div className="flex items-center gap-2.5">
                          <div className={`w-9 h-9 rounded-full ${getAvatarColor()} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}>
                            {getUserInitials()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold truncate" style={{ color: 'var(--gp-ink)' }}>
                              {user.fullName || 'User'}
                            </p>
                            <p className="text-xs truncate" style={{ color: 'var(--gp-ink-subtle)' }}>
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <Link
                        to="/subscriptions"
                        onClick={() => setShowUserMenu(false)}
                        className="gp-dropdown-item flex items-center gap-2.5"
                      >
                        <FontAwesomeIcon icon={faCrown} style={{ color: 'var(--gp-accent)' }} />
                        <span>{t('nav.mySubscriptions')}</span>
                      </Link>
                      {!freeModeEnabled && (
                        <Link
                          to="/billing"
                          onClick={() => setShowUserMenu(false)}
                          className="gp-dropdown-item flex items-center gap-2.5"
                        >
                          <FontAwesomeIcon icon={faCreditCard} style={{ color: 'var(--gp-brand)' }} />
                          <span>{t('billing.title')}</span>
                        </Link>
                      )}
                      
                      <Link
                        to="/settings"
                        onClick={() => setShowUserMenu(false)}
                        className="gp-dropdown-item flex items-center gap-2.5"
                      >
                        <FontAwesomeIcon icon={faCog} style={{ color: 'var(--gp-ink-subtle)' }} />
                        <span>{t('nav.settings')}</span>
                      </Link>
                      
                      {user.isAdmin && (
                        <>
                          <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--gp-ink-subtle)' }}>
                            Admin
                          </div>
                          <Link
                            to="/admin/certifications"
                            onClick={() => setShowUserMenu(false)}
                            className="gp-dropdown-item flex items-center gap-2.5"
                          >
                            <FontAwesomeIcon icon={faCertificate} style={{ color: 'var(--gp-brand)' }} />
                            <span>Manage Certifications</span>
                          </Link>
                          <Link
                            to="/admin/pricing"
                            onClick={() => setShowUserMenu(false)}
                            className="gp-dropdown-item flex items-center gap-2.5"
                          >
                            <FontAwesomeIcon icon={faDollarSign} style={{ color: 'var(--gp-success)' }} />
                            <span>Pricing Config</span>
                          </Link>
                          <Link
                            to="/certifications"
                            onClick={() => setShowUserMenu(false)}
                            className="gp-dropdown-item flex items-center gap-2.5"
                          >
                            <FontAwesomeIcon icon={faQuestionCircle} style={{ color: 'var(--gp-brand)' }} />
                            <span>Manage Questions</span>
                          </Link>
                          <Link
                            to="/admin/subscriptions"
                            onClick={() => setShowUserMenu(false)}
                            className="gp-dropdown-item flex items-center gap-2.5"
                          >
                            <FontAwesomeIcon icon={faCreditCard} style={{ color: 'var(--gp-success)' }} />
                            <span>Manage Subscriptions</span>
                          </Link>
                          <Link
                            to="/admin/settings"
                            onClick={() => setShowUserMenu(false)}
                            className="gp-dropdown-item flex items-center gap-2.5"
                          >
                            <FontAwesomeIcon icon={faCog} style={{ color: 'var(--gp-ink-subtle)' }} />
                            <span>{t('nav.settings')}</span>
                          </Link>
                          <Link
                            to="/admin/coupons"
                            onClick={() => setShowUserMenu(false)}
                            className="gp-dropdown-item flex items-center gap-2.5"
                          >
                            <FontAwesomeIcon icon={faTicketAlt} style={{ color: 'var(--gp-accent)' }} />
                            <span>Coupons</span>
                          </Link>
                        </>
                      )}
                      
                      <div className="border-t my-1" style={{ borderColor: 'var(--gp-border)' }}></div>
                      
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          handleLogout();
                        }}
                        className="gp-dropdown-item flex items-center gap-2.5 w-full hover:!bg-[var(--gp-accent-soft)] hover:!text-[var(--gp-accent)]"
                        style={{ color: 'var(--gp-accent)' }}
                      >
                        <FontAwesomeIcon icon={faSignOutAlt} />
                        <span>{t('nav.logout')}</span>
                      </button>
                    </div>
                  )}
                </div>
                <LanguageSwitcher />
                <ThemeToggle />
              </>
            ) : (
              <>
                <Link to="/learn" className="gp-nav-link">
                  <FontAwesomeIcon icon={faRobot} />
                  <span>{t('nav.learn')}</span>
                </Link>
                <Link to="/certifications" className="gp-nav-link">
                  <FontAwesomeIcon icon={faGraduationCap} />
                  <span>{t('nav.certifications')}</span>
                </Link>
                {!freeModeEnabled && (
                  <Link to="/pricing" className="gp-nav-link">
                    <FontAwesomeIcon icon={faDollarSign} />
                    <span>{t('nav.pricing')}</span>
                  </Link>
                )}
                <Link to="/login" className="gp-nav-link">
                  <FontAwesomeIcon icon={faSignInAlt} />
                  <span>{t('nav.login')}</span>
                </Link>
                <Link to="/register" className="gp-nav-cta">
                  <FontAwesomeIcon icon={faUserPlus} />
                  <span>{t('auth.register')}</span>
                </Link>
                <LanguageSwitcher />
                <ThemeToggle />
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-1">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="gp-nav-link p-1.5"
              aria-label="Toggle menu"
            >
              <FontAwesomeIcon icon={showMobileMenu ? faTimes : faBars} className="text-lg" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div
            ref={mobileMenuRef}
            className="md:hidden py-3 animate-in slide-in-from-top duration-200"
            style={{ borderTop: '1px solid var(--gp-border)' }}
          >
            {user ? (
              <div className="space-y-1">
                <Link
                  to="/dashboard"
                  onClick={() => setShowMobileMenu(false)}
                  className="gp-nav-link w-full"
                >
                  <FontAwesomeIcon icon={faHome} />
                  <span>Dashboard</span>
                </Link>
                <Link
                  to="/learn"
                  onClick={() => setShowMobileMenu(false)}
                  className="gp-nav-link w-full"
                >
                  <FontAwesomeIcon icon={faRobot} />
                  <span>{t('nav.learn')}</span>
                </Link>
                {/* Mobile Certifications Section */}
                <div className="px-2.5 py-2">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--gp-ink)' }}>
                      <FontAwesomeIcon icon={faCertificate} style={{ color: 'var(--gp-brand)' }} />
                      <span>{t('nav.certifications')}</span>
                    </h3>
                    <Link
                      to="/certifications"
                      onClick={() => setShowMobileMenu(false)}
                      className="text-xs font-semibold hover:underline"
                      style={{ color: 'var(--gp-brand)' }}
                    >
                      View All
                    </Link>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto space-y-2.5">
                    {Array.from(examsBySubcategory.entries()).map(([subcategory, exams]) => (
                      <div key={subcategory}>
                        {/* Subcategory Header */}
                        {subcategory !== 'Other' && (
                          <div className="mb-1 px-1">
                            <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--gp-ink-subtle)' }}>
                              {subcategory}
                            </h4>
                          </div>
                        )}
                        {/* Exams Grid */}
                        <div className="grid grid-cols-2 gap-1">
                          {exams.map((exam) => (
                            <Link
                              key={exam.id}
                              to={getExamUrl(exam.id, exam.name)}
                              onClick={() => setShowMobileMenu(false)}
                              className="flex items-center gap-2 p-1.5 rounded-md transition-colors hover:bg-[var(--gp-brand-soft)]"
                            >
                              {/* Logo */}
                              <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
                                {exam.badge_image ? (
                                  <img
                                    src={`/${exam.badge_image}`}
                                    alt={exam.name}
                                    className="w-7 h-7 object-contain"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      const fallback = target.nextElementSibling as HTMLElement;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div
                                  className={`w-7 h-7 rounded-md flex items-center justify-center ${exam.badge_image ? 'hidden' : ''}`}
                                  style={examPlaceholderStyle}
                                >
                                  <FontAwesomeIcon icon={faGraduationCap} className="text-white text-[10px]" />
                                </div>
                              </div>
                              {/* Name */}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate" style={{ color: 'var(--gp-ink)' }}>
                                  {exam.name}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {!freeModeEnabled && (
                  <Link
                    to="/billing"
                    onClick={() => setShowMobileMenu(false)}
                    className="gp-nav-link w-full"
                  >
                    <FontAwesomeIcon icon={faCreditCard} style={{ color: 'var(--gp-brand)' }} />
                    <span>{t('billing.title')}</span>
                  </Link>
                )}
                <Link
                  to="/settings"
                  onClick={() => setShowMobileMenu(false)}
                  className="gp-nav-link w-full"
                >
                  <FontAwesomeIcon icon={faCog} style={{ color: 'var(--gp-ink-subtle)' }} />
                  <span>{t('nav.settings')}</span>
                </Link>
                {user.isAdmin && (
                  <>
                    <div className="px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--gp-ink-subtle)' }}>
                      Admin
                    </div>
                    <Link
                      to="/admin/certifications"
                      onClick={() => setShowMobileMenu(false)}
                      className="gp-nav-link w-full"
                    >
                      <FontAwesomeIcon icon={faCertificate} style={{ color: 'var(--gp-brand)' }} />
                      <span>Manage Certifications</span>
                    </Link>
                    <Link
                      to="/admin/pricing"
                      onClick={() => setShowMobileMenu(false)}
                      className="gp-nav-link w-full"
                    >
                      <FontAwesomeIcon icon={faDollarSign} style={{ color: 'var(--gp-success)' }} />
                      <span>Pricing Config</span>
                    </Link>
                    <Link
                      to="/certifications"
                      onClick={() => setShowMobileMenu(false)}
                      className="gp-nav-link w-full"
                    >
                      <FontAwesomeIcon icon={faQuestionCircle} style={{ color: 'var(--gp-brand)' }} />
                      <span>Manage Questions</span>
                    </Link>
                    <Link
                      to="/admin/subscriptions"
                      onClick={() => setShowMobileMenu(false)}
                      className="gp-nav-link w-full"
                    >
                      <FontAwesomeIcon icon={faCreditCard} style={{ color: 'var(--gp-success)' }} />
                      <span>Manage Subscriptions</span>
                    </Link>
                    <Link
                      to="/admin/settings"
                      onClick={() => setShowMobileMenu(false)}
                      className="gp-nav-link w-full"
                    >
                      <FontAwesomeIcon icon={faCog} style={{ color: 'var(--gp-ink-subtle)' }} />
                      <span>{t('nav.settings')}</span>
                    </Link>
                    <Link
                      to="/admin/coupons"
                      onClick={() => setShowMobileMenu(false)}
                      className="gp-nav-link w-full"
                    >
                      <FontAwesomeIcon icon={faTicketAlt} style={{ color: 'var(--gp-accent)' }} />
                      <span>Coupons</span>
                    </Link>
                  </>
                )}
                <div className="border-t my-2" style={{ borderColor: 'var(--gp-border)' }}></div>
                <div className="px-2.5 py-2">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-9 h-9 rounded-full ${getAvatarColor()} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}>
                      {getUserInitials()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--gp-ink)' }}>
                        {user.fullName || 'User'}
                      </p>
                      <p className="text-xs truncate" style={{ color: 'var(--gp-ink-subtle)' }}>
                        {user.email}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowMobileMenu(false);
                    handleLogout();
                  }}
                  className="w-full gp-nav-link"
                  style={{ color: 'var(--gp-accent)' }}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <Link
                  to="/learn"
                  onClick={() => setShowMobileMenu(false)}
                  className="gp-nav-link w-full"
                >
                  <FontAwesomeIcon icon={faRobot} />
                  <span>{t('nav.learn')}</span>
                </Link>
                {/* Mobile Certifications Section */}
                <div className="px-2.5 py-2">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--gp-ink)' }}>
                      <FontAwesomeIcon icon={faCertificate} style={{ color: 'var(--gp-brand)' }} />
                      <span>{t('nav.certifications')}</span>
                    </h3>
                    <Link
                      to="/certifications"
                      onClick={() => setShowMobileMenu(false)}
                      className="text-xs font-semibold hover:underline"
                      style={{ color: 'var(--gp-brand)' }}
                    >
                      View All
                    </Link>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto space-y-2.5">
                    {Array.from(examsBySubcategory.entries()).map(([subcategory, exams]) => (
                      <div key={subcategory}>
                        {/* Subcategory Header */}
                        {subcategory !== 'Other' && (
                          <div className="mb-1 px-1">
                            <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--gp-ink-subtle)' }}>
                              {subcategory}
                            </h4>
                          </div>
                        )}
                        {/* Exams Grid */}
                        <div className="grid grid-cols-2 gap-1">
                          {exams.map((exam) => (
                            <Link
                              key={exam.id}
                              to={getExamUrl(exam.id, exam.name)}
                              onClick={() => setShowMobileMenu(false)}
                              className="flex items-center gap-2 p-1.5 rounded-md transition-colors hover:bg-[var(--gp-brand-soft)]"
                            >
                              {/* Logo */}
                              <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
                                {exam.badge_image ? (
                                  <img
                                    src={`/${exam.badge_image}`}
                                    alt={exam.name}
                                    className="w-7 h-7 object-contain"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      const fallback = target.nextElementSibling as HTMLElement;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div
                                  className={`w-7 h-7 rounded-md flex items-center justify-center ${exam.badge_image ? 'hidden' : ''}`}
                                  style={examPlaceholderStyle}
                                >
                                  <FontAwesomeIcon icon={faGraduationCap} className="text-white text-[10px]" />
                                </div>
                              </div>
                              {/* Name */}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate" style={{ color: 'var(--gp-ink)' }}>
                                  {exam.name}
                                </p>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {!freeModeEnabled && (
                  <Link
                    to="/pricing"
                    onClick={() => setShowMobileMenu(false)}
                    className="gp-nav-link w-full"
                  >
                    <FontAwesomeIcon icon={faDollarSign} />
                    <span>{t('nav.pricing')}</span>
                  </Link>
                )}
                <Link
                  to="/login"
                  onClick={() => setShowMobileMenu(false)}
                  className="gp-nav-link w-full"
                >
                  <FontAwesomeIcon icon={faSignInAlt} />
                  <span>{t('nav.login')}</span>
                </Link>
                <Link
                  to="/register"
                  onClick={() => setShowMobileMenu(false)}
                  className="gp-nav-cta w-full justify-center mt-1"
                >
                  <FontAwesomeIcon icon={faUserPlus} />
                  <span>{t('auth.register')}</span>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
